import React, { ComponentProps, FC } from "react";
import * as _ from "lodash";
import { useLocation } from "react-router-dom";

import {
  ChartEntity,
  ChartFilters,
  ChartFilterType,
  Charts,
  ChartType,
  DataDescriptorEntity,
  DIMGroupEntity,
  HerdGroupEntity,
  PenEntity
} from "@ctra/api";

import { Col, Row, Switch, Space, Typography, SwitchWithLabel } from "@ctra/components";
import { useChartContext } from "@ctra/charts";
import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { useGoogleAnalytics } from "@ctra/analytics";

import { URIState } from "@routes";

import {
  ChartViewFilter,
  DIMGroupFilter,
  DateMethod,
  GAActions,
  HerdGroupFilter,
  ISODurationFilter,
  useChartAPI,
  useChartFilters,
  PenFilter
} from "@chart";

import styles from "./Toolbar.module.less";

const { Text } = Typography;

const {
  chart: {
    logScaling,
    filters: { eventToggle }
  }
} = Content;

/**
 * Chart filtering toolbar
 * @returns {JSX.Element}
 * @constructor
 */
export const Toolbar: FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation<URIState>();
  const { trackEvent, label } = useGoogleAnalytics<{ label: string }>();

  const {
    metricVariants,
    charts,
    api: { changeView },
    meta: { supportedViews }
  } = useChartAPI();

  const {
    filters,
    timePeriod,
    bucketSize,
    isoDuration,
    api: { getMetadata, setISODuration, setFilter, setTimePeriod, setCurrentDateMethod }
  } = useChartFilters();

  const {
    values: {
      viewOptions: { showEvents, showEventToggle },
      config
    },
    api: { toggleEvents, setContextConfig }
  } = useChartContext();

  const [{ entity: mainChart }] = charts;
  const { view } = mainChart;
  const [mainVariant] = metricVariants;
  const { supportedISODurations } = getMetadata(mainVariant.id);

  /**
   * Only set this flag when every chart supports it
   * @type {boolean}
   */
  const isMultiDataIntervalSupported = _.every(metricVariants, (dataDescriptorEntity) =>
    _.get(dataDescriptorEntity, ["flags", "isMultiDataIntervalSupported"], false)
  );

  /**
   * Handle herd group changes
   * @param {DataDescriptorEntity["id"]} variantID
   * @param herdGroupIDList
   */
  const onHerdGroupChange = (
    variantID: DataDescriptorEntity["id"],
    herdGroupIDList: Array<HerdGroupEntity["id"]>
  ) => setFilter(variantID, ChartFilterType.herdGroup, herdGroupIDList);

  /**
   * Handle DIM group changes
   * @param {DataDescriptorEntity["id"]} variantID
   * @param {Array<string>} dimGroupIDList
   */
  const onDIMGroupChange = (
    variantID: DataDescriptorEntity["id"],
    dimGroupIDList: Array<DIMGroupEntity["id"]>
  ) => setFilter(variantID, ChartFilterType.dimGroup, dimGroupIDList);

  /**
   * Handle pen changes
   * @param {DataDescriptorEntity["id"]} variantID
   * @param {Array<PenEntity["id"]>} penNameList
   */
  const onPenChange = (variantID: DataDescriptorEntity["id"], penNameList: Array<PenEntity["id"]>) =>
    setFilter(variantID, ChartFilterType.penName, penNameList);

  /**
   * Handle ISO duration changes
   * @param {<ISODuration>} duration
   */
  const onDurationChange: ComponentProps<typeof ISODurationFilter>["onDurationChange"] = (duration) => {
    setCurrentDateMethod(DateMethod.iso);
    setISODuration(duration);
  };

  /**
   * Handle custom date range changes
   * @param {ChartTimePeriod} timePeriod
   */
  const onTimePeriodChange: ComponentProps<typeof ISODurationFilter>["onTimePeriodChange"] = (timePeriod) => {
    setCurrentDateMethod(DateMethod.custom);
    setTimePeriod(timePeriod);
  };

  /**
   * Handle chart view changes
   * @param {ChartType} view
   */
  const onViewChange: ComponentProps<typeof ChartViewFilter>["onViewChange"] = (view: ChartType) =>
    changeView(view);

  /**
   * Handler to toggle events on the chart
   * @param {boolean} checked
   */
  const onToggleEvents: ComponentProps<typeof Switch>["onChange"] = (checked) => {
    trackEvent(GAActions.toggleEventAnnotations, { label: `${label} - ${checked ? "on" : "off"}` });
    toggleEvents(_.mapValues(showEvents, () => checked));
  };

  /**
   * Tell if a filter is multi-selectable
   * @param {ChartEntity["id"]} chartID
   * @param {ChartFilterType} filterType
   * @returns {boolean}
   */
  const isMultiSelectable = (chartID: ChartEntity["id"], filterType: ChartFilterType): boolean => {
    const chart = _.find(charts, (chart) => chart.entity.id === chartID);

    if (!chart) {
      throw new Error(`Chart with ID ${chartID} not found`);
    }

    const {
      entity: { filters: filterDefinitions }
    } = chart;

    const definition = _.find(filterDefinitions, ["name", filterType]);

    return !!definition && definition.isMultiSelectable;
  };

  /**
   * Tell if the chart is zoomed
   * @type {boolean}
   */
  const isZoomed = !_.isEmpty(state?.zoom);

  return (
    <Row
      gutter={[16, 8]}
      justify="end"
      align="middle"
      className={styles.Wrapper}
      onClick={(e) => e.stopPropagation()}
    >
      <Col>
        <SwitchWithLabel
          defaultChecked={_.get(config, ["yAxis", "type"]) === "log"}
          onChange={(checked) => {
            setContextConfig((old) => ({
              ...old,
              yAxis: {
                ...old.yAxis,
                type: checked ? "log" : void 0
              }
            }));
          }}
          size="small"
          label={t<string>(logScaling)}
        />
      </Col>
      {isZoomed && (
        <>
          {showEventToggle && (
            <Col style={{ lineHeight: 0 }}>
              <Space align="center">
                <Switch size="small" checked={_.every(showEvents)} onChange={onToggleEvents} />
                <Text>{t(eventToggle)}</Text>
              </Space>
            </Col>
          )}
          {_.map(metricVariants, ({ id }, idx) => {
            const { herdGroupID, dimGroupKey, penName } = _.get(filters, [id], {} as ChartFilters);
            const { supportedfarmIDs } = getMetadata(id);

            const {
              entity: { id: chartID }
            } = charts[idx];

            return (
              <>
                {!!dimGroupKey && (
                  <Col>
                    <DIMGroupFilter
                      multiple={isMultiSelectable(chartID, ChartFilterType.dimGroup)}
                      farmIDList={supportedfarmIDs}
                      selection={dimGroupKey}
                      onDIMGroupChange={_.partial(onDIMGroupChange, id)}
                      data-testid="dim-group-filter"
                    />
                  </Col>
                )}
                {!!herdGroupID && (
                  <Col>
                    <HerdGroupFilter
                      farmIDList={supportedfarmIDs}
                      selection={herdGroupID}
                      multiple={isMultiSelectable(chartID, ChartFilterType.herdGroup)}
                      onHerdGroupChange={_.partial(onHerdGroupChange, id)}
                      data-testid="herd-group-filter"
                    />
                  </Col>
                )}
                {!!penName && (
                  <Col>
                    <PenFilter
                      farmIDList={supportedfarmIDs}
                      selection={penName}
                      multiple={isMultiSelectable(chartID, ChartFilterType.penName)}
                      onPenChange={_.partial(onPenChange, id)}
                      data-testid="pen-filter"
                    />
                  </Col>
                )}
              </>
            );
          })}
          {_.keys(supportedViews).length > 1 && (
            <Col>
              <ChartViewFilter
                views={_.keys(supportedViews) as ChartType[]}
                view={view}
                onViewChange={onViewChange}
              />
            </Col>
          )}
          {isMultiDataIntervalSupported && Charts.utils.hasBucketSizeSupport(view) && (
            <Col>
              <ISODurationFilter
                durations={supportedISODurations}
                duration={isoDuration}
                timePeriod={timePeriod}
                bucketSize={bucketSize}
                buckets={Charts.utils.hasBucketSizeSupport(view) ? void 0 : []}
                onDurationChange={onDurationChange}
                onTimePeriodChange={onTimePeriodChange}
              />
            </Col>
          )}
        </>
      )}
    </Row>
  );
};
